import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import Typography from '../Typography';
import UploadFileIcon from '../Icons/UploadFileIcon';
import LoadingProgress from '../LoadingProgress';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';
import { imageCategories } from '../../utilities';
import { TERRAIN_ALL } from '../../constants/eventTypes';
import useImagePreloader from '../../hooks/useImagePreloader';
import { useEventCategories } from '../../hooks/useEventCategories';

const ImageSelectionModal = ({ onClose, onImageSelect, eventType }) => {
  const { t } = useTranslation(['createEvent', 'eventCategories']);
  const { eventTypes } = useEventCategories();
  const [selectedCategory, setSelectedCategory] = useState(eventType || TERRAIN_ALL);
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Preparar todas las imágenes y las prioritarias
  const allImages = useMemo(() => Object.values(imageCategories).flat(), []);
  const priorityImages = useMemo(() => {
    return eventType && imageCategories[eventType] ? imageCategories[eventType] : [];
  }, [eventType]);

  // Usar el hook de precarga optimizada
  const {
    isImageLoaded,
    isPreloading,
    progress,
    loadedCount,
    failedCount
  } = useImagePreloader(allImages, {
    priorityImages,
    batchSize: 6,
    batchDelay: 150
  });

  // Crear categoryLabels dinámicamente usando el hook
  const categoryLabels = useMemo(() => {
    const labels = {
      all: t('createEvent:eventForm.eventImage.allImages', 'Todas las imágenes')
    };

    eventTypes.forEach(type => {
      labels[type.key] = type.label;
    });

    return labels;
  }, [eventTypes, t]);

  // Función para obtener todas las imágenes
  const getAllImages = () => {
    return Object.values(imageCategories).flat();
  };

  // Función para obtener las imágenes según la categoría seleccionada
  const getImagesForCategory = (category) => {
    if (category === TERRAIN_ALL) {
      return getAllImages();
    }
    return imageCategories[category] || [];
  };



  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleDropZoneClick = () => {
    document.getElementById('file-upload').click();
  };

  const handleFileUpload = (file) => {
    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      alert(t('eventForm.eventImage.supportedFiles'));
      return;
    }

    // Validar tamaño (máximo 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert(t('eventForm.eventImage.largeFile'));
      return;
    }

    onImageSelect({ type: 'file', data: file });
    onClose();
  };

  const handlePresetImageSelect = (imageUrl) => {
    onImageSelect({ type: 'preset', data: imageUrl });
    onClose();
  };

  return (
    <>
      <LoadingProgress
        progress={progress}
        loadedCount={loadedCount}
        totalImages={allImages.length}
        failedCount={failedCount}
        isVisible={isPreloading && progress < 100}
        title="Precargando imágenes..."
      />
      <Modal
        title={t('eventForm.eventImage.selectImage', 'Elegir imagen')}
        onClose={onClose}
        maxWidth="800px"
        maxHeight="80vh"
        overflowY="hidden"
      >
        <ModalContent>
          {/* Área de carga de archivos */}
          <UploadSection>
            <DropZone
              $dragActive={dragActive}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={handleDropZoneClick}
            >
              <UploadFileIcon fill={theme.colors.defaultStrong} height="24" width="24" />
              {/* {getEventTypeSvgIcon(eventType, theme.colors.defaultSubtle)} */}
              <UploadContent>
                <Typography
                  $variant="body-1-semibold"
                  color={theme.colors.defaultStrong}
                  style={{ textAlign: 'center' }}
                >
                  {t('eventForm.eventImage.dragDrop')}
                </Typography>
                <Typography
                  $variant="body-2-medium"
                  color={theme.colors.defaultSubtle}
                  style={{ textAlign: 'center' }}
                >
                  {t('eventForm.eventImage.dragDropSubtext')}
                </Typography>
                <HiddenInput
                  type="file"
                  id="file-upload"
                  onChange={handleFileChange}
                  accept="image/*"
                />
              </UploadContent>
            </DropZone>
          </UploadSection>

          {/* Categorías */}
          <CategorySection>
            <CategoryTabs>
              {Object.keys(categoryLabels).map((category) => (
                <CategoryTab
                  key={category}
                  $active={selectedCategory === category}
                  onClick={() => setSelectedCategory(category)}
                >
                  <CategoryTabLabel
                    $variant="body-2-semibold"
                    $active={selectedCategory === category}
                  // color={selectedCategory === category ? theme.colors.inverseMain : theme.colors.defaultMain}
                  >
                    {categoryLabels[category]}
                  </CategoryTabLabel>
                </CategoryTab>
              ))}
            </CategoryTabs>

            {/* Grid de imágenes */}
            <GridBlock>
              <GridWrapper>

                <ImageGrid>
                  {getImagesForCategory(selectedCategory).map((imageUrl, index) => (
                    <ImageCard
                      //key={`${selectedCategory}-${index}`}
                      key={imageUrl}
                      onClick={() => handlePresetImageSelect(imageUrl)}
                    >
                      {isImageLoaded(imageUrl) ? (
                        <PresetImage src={imageUrl} alt={`${categoryLabels[selectedCategory]} ${index + 1}`} />
                      ) : (
                        <ImagePlaceholder>
                          <LoadingSpinner />
                        </ImagePlaceholder>
                      )}
                    </ImageCard>
                  ))}
                </ImageGrid>
              </GridWrapper>

            </GridBlock>
          </CategorySection>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ImageSelectionModal;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const UploadSection = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.sizing.sm};
`;

const DropZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.sm};
  border: 1px solid ${({ theme, $dragActive }) =>
    $dragActive ? theme.border.defaultStrong : theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  background-color: ${({ theme, $dragActive }) =>
    $dragActive ? theme.fill.defaultWeak : theme.fill.defaultSubtle};
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 120px;

  &:hover {
    border-color: ${({ theme }) => theme.border.defaultStrong};
    background-color: ${({ theme }) => theme.fill.primaryMain};
  }
`;

const UploadContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const HiddenInput = styled.input`
  display: none;
`;

const CategorySection = styled.div`
  display: flex;
  flex-direction: row;
  // gap: ${({ theme }) => theme.sizing.xs};
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.xxs};
  overflow-x: auto;
  padding: 0px ${({ theme }) => theme.sizing.sm};
  padding-bottom: ${({ theme }) => theme.sizing.sm};
  flex-shrink: 0;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.fill.defaultSubtle};
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.defaultWeak};
    border-radius: 2px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    align-items: center;
    padding-bottom: ${({ theme }) => theme.sizing.xs};
  }
`;

const CategoryTab = styled.button`
  height: 28px;
  padding: 0px ${({ theme }) => theme.sizing.xs};
  border-radius: ${({ theme }) => theme.radius.md};
  background-color: ${({ theme, $active }) =>
    $active ? theme.fill.defaultStrong : theme.fill.defaultMain};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  border: none;
  
  &:hover {
    background-color: ${({ theme, $active }) =>
    $active ? theme.fill.defaultStrong : theme.fill.defaultWeak};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    background-color: ${({ theme, $active }) =>
    $active ? theme.fill.inverseMain : theme.fill.defaultWeak};

    &:hover {
      background-color: ${({ theme, $active }) =>
    $active ? theme.fill.inverseStrong : theme.fill.defaultStrong};
    }
  }
`;

const CategoryTabLabel = styled(Typography)`
  color: ${({ theme, $active }) =>
    $active ? theme.colors.defaultMain : theme.colors.defaultWeak};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    color: ${({ theme, $active }) =>
    $active ? theme.colors.inverseMain : theme.colors.defaultWeak};
  }
`;



const GridBlock = styled.div`
  display: flex;
  flex-direction: column;
  padding:  0px ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-top: ${({ theme }) => theme.sizing.sm};
  }
`;

const GridWrapper = styled.div`
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    max-height: 50vh; /* Usar viewport height en lugar de altura fija */
    min-height: 200px; /* Altura mínima para asegurar visibilidad */
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  gap: ${({ theme }) => theme.sizing.xxs};
  padding-bottom: ${({ theme }) => theme.sizing.sm};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
    // max-height: 300px;
    gap: ${({ theme }) => theme.sizing.xxs};
    // grid-template-columns: repeat(auto-fill, minmax(6rem, 1fr)); /* Reducir tamaño mínimo */
    // grid-template-rows: repeat(auto-fit, minmax(90px, auto)); /* Filas más pequeñas en móvil */
  }
`;

const ImageCard = styled.div`
  aspect-ratio: 4 / 3;
  width: 100%;
  height: 100% !important;
  overflow: hidden;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.xs};
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  position: relative;
`;

const PresetImage = styled.img`
  // aspect-ratio: 4 / 3;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.2s ease;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  
  &:hover {
    // border: 1px solid ${({ theme }) => theme.colors.primaryMain};
    opacity: 0.9;
    transform: scale(1.02);
  }
`;

const ImagePlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid ${({ theme }) => theme.border.defaultWeak};
  border-top: 2px solid ${({ theme }) => theme.colors.primaryMain};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;